/**
 * ============================================================
 * 全局 Popper 定位修复插件（适配 html zoom 等比缩放）
 * ------------------------------------------------------------
 * 背景：
 *   adaptive.js 对 PC 端 <html> 设置 zoom（0.66~1.5）等比缩放，
 *   Element UI 弹层（下拉/悬浮菜单/日期面板等）挂载在 body 下，
 *   popper.js 按缩放后视口坐标写入 style.left / top / transform，
 *   渲染时又被 zoom 二次缩放，导致弹层相对触发元素偏移 (1 - scale) 比例。
 *
 *   此外，Chrome 在非 1 的 html zoom 下会将 dropdown popper 内的
 *   文本错误渲染成竖排（一行一个字），这是渲染层面的 bug，
 *   CSS writing-mode 无法修复。
 *
 * 方案：
 *   1) 对 .el-dropdown__popper 设置反向 zoom（1/scale），中和继承的
 *      html zoom，使 popper 内部有效缩放回到 1.0，文本正常水平渲染。
 *      反向 zoom 后 popper.js 的位置坐标无需再做除法修正。
 *   2) 对其他弹层（select/cascader/picker/tooltip 等），监听 style 变化，
 *      将 popper.js 写入的 left / top / transform 数值除以当前 scale，
 *      使弹层最终渲染位置与触发元素对齐。修正值通过 WeakMap 缓存实现
 *      幂等，避免自身修正引发死循环。
 *
 * 覆盖范围：
 *   .el-dropdown__popper（反向 zoom 修复文字竖排 + 跳过位置修正）
 *   .el-select-dropdown / .el-cascader__dropdown / .el-picker-panel
 *   .el-autocomplete-suggestion / .el-tooltip__popper / .el-popper
 *
 * 注意：
 *   - Pad / Mobile 断点 scale=1，不触发修正；
 *   - 本插件须在 './plugins/adaptive' 之后引入（依赖 window.__ADAPTIVE__）。
 * ============================================================
 */

const POPPER_SELECTOR = [
  '.el-select-dropdown',
  '.el-dropdown__popper',
  '.el-cascader__dropdown',
  '.el-picker-panel',
  '.el-autocomplete-suggestion',
  '.el-tooltip__popper',
  '.el-popper',
].join(',')

/** 缓存每个弹层最近一次修正后的值（幂等依据） */
const fixedCache = new WeakMap()

/** 当前缩放比例（读取 adaptive 运行时状态） */
function getScale() {
  try {
    return (window.__ADAPTIVE__ && window.__ADAPTIVE__.getScale()) || 1
  } catch (e) {
    return 1
  }
}

/** 解析数值：'123.5px' -> 123.5，失败返回 NaN */
function toNum(v) {
  return parseFloat(v)
}

/**
 * 判断元素是否为 dropdown popper（需要反向 zoom 修复文字竖排）
 */
function isDropdownPopper(el) {
  return el.classList && el.classList.contains('el-dropdown__popper')
}

/**
 * 对 dropdown popper 设置反向 zoom，中和 html zoom 的影响。
 * Chrome 在非 1 的 html zoom 下会把 dropdown popper 内的文本
 * 错误渲染成竖排，通过设置 zoom = 1/scale 使 popper 内部的
 * 有效缩放回到 1.0，文本恢复正常水平渲染。
 * 返回 true 表示该元素是 dropdown popper（已处理 zoom）。
 */
function fixDropdownZoom(el, scale) {
  if (!isDropdownPopper(el)) return false
  const inverseZoom = 1 / scale
  const expected = inverseZoom.toFixed(4)
  if (el.style.zoom !== expected) {
    el.style.zoom = expected
  }
  return true
}

/**
 * 修正单个弹层元素的位置。
 * 对 dropdown popper：先设置反向 zoom 修复文字竖排，然后跳过位置修正
 * （因为反向 zoom 已使 popper.js 的坐标在视觉上正确）。
 * 对其他弹层：执行原有的位置除法修正。
 * 返回 true 表示本次发生了修正写入。
 */
function fixPopper(el) {
  const scale = getScale()
  if (!scale || scale === 1) return false

  // dropdown popper：反向 zoom 修复文字竖排，不做位置修正
  if (fixDropdownZoom(el, scale)) {
    return false
  }

  const style = el.style
  const cached = fixedCache.get(el) || {}
  let changed = false

  // 1) left / top（select / cascader / autocomplete / picker-panel 等）
  if (style.left && style.left !== 'auto' && style.left !== cached.left) {
    const px = toNum(style.left)
    if (!isNaN(px) && px !== 0) {
      style.left = `${px / scale}px`
      cached.left = style.left
      changed = true
    } else {
      cached.left = style.left
    }
  }
  if (style.top && style.top !== 'auto' && style.top !== cached.top) {
    const px = toNum(style.top)
    if (!isNaN(px) && px !== 0) {
      style.top = `${px / scale}px`
      cached.top = style.top
      changed = true
    } else {
      cached.top = style.top
    }
  }

  // 2) transform translate3d / translate（tooltip / popover / select 等）
  const tf = style.transform
  if (tf && tf !== 'none' && tf !== cached.transform) {
    const fixedTf = fixTransform(tf, scale)
    if (fixedTf !== tf) {
      style.transform = fixedTf
      cached.transform = fixedTf
      changed = true
    } else {
      cached.transform = tf
    }
  }

  if (changed) {
    fixedCache.set(el, cached)
  }
  return changed
}

/** 将 transform 中的 translate 数值除以 scale，其余保留 */
function fixTransform(tf, scale) {
  const m = tf.match(
    /(translate3d\()\s*([-\d.]+)px\s*,\s*([-\d.]+)px\s*,\s*([-\d.]+)px\s*\)/
  ) || tf.match(
    /(translate\()\s*([-\d.]+)px\s*,\s*([-\d.]+)px\s*\)/
  )
  if (!m) return tf
  if (m[1] === 'translate3d(') {
    const x = toNum(m[2]) / scale
    const y = toNum(m[3]) / scale
    const z = m[4]
    return `translate3d(${x}px, ${y}px, ${z}px)${tf.slice(m[0].length)}`
  }
  const x = toNum(m[2]) / scale
  const y = toNum(m[3]) / scale
  return `translate(${x}px, ${y}px)${tf.slice(m[0].length)}`
}

/* ---------- 节流批处理 ---------- */
let pendingEls = []
let rafPending = false

function schedule(el) {
  if (pendingEls.indexOf(el) === -1) pendingEls.push(el)
  if (rafPending) return
  rafPending = true
  requestAnimationFrame(() => {
    rafPending = false
    const list = pendingEls
    pendingEls = []
    list.forEach(fixPopper)
  })
}

/* ---------- 初始化 ---------- */
let observer = null

function initFixPopper() {
  if (!window.MutationObserver || observer) return

  const body = document.body
  if (!body) {
    // body 尚未就绪，等 DOMContentLoaded 再初始化
    document.addEventListener('DOMContentLoaded', initFixPopper, { once: true })
    return
  }

  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'style') continue
      const el = mutation.target
      if (el.nodeType !== 1) continue
      if (el.matches && el.matches(POPPER_SELECTOR)) {
        schedule(el)
      }
    }
  })

  observer.observe(body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['style'],
  })

  // 窗口变化（zoom 变化）后强制重刷已显示的弹层
  window.addEventListener('resize', () => {
    const scale = getScale()
    const els = document.querySelectorAll(POPPER_SELECTOR)
    els.forEach((el) => {
      // dropdown popper：更新反向 zoom
      if (scale !== 1 && isDropdownPopper(el)) {
        fixDropdownZoom(el, scale)
      }
      const style = el.style
      if (style.display !== 'none' && (style.left || style.top || (style.transform && style.transform !== 'none'))) {
        schedule(el)
      }
    })
  })

  // 断点切换（PC <-> Pad/Mobile）后重刷
  window.addEventListener('adaptive-bpchange', () => {
    const scale = getScale()
    const els = document.querySelectorAll(POPPER_SELECTOR)
    els.forEach((el) => {
      // dropdown popper：更新反向 zoom（Pad/Mobile scale=1 时清除）
      if (isDropdownPopper(el)) {
        if (scale !== 1) {
          fixDropdownZoom(el, scale)
        } else {
          el.style.zoom = ''
        }
      }
      const style = el.style
      if (style.display !== 'none') schedule(el)
    })
  })
}

if (typeof window !== 'undefined') {
  initFixPopper()
}

export default { initFixPopper, fixPopper }
